import React, { useState, useEffect } from 'react';
import TaskCard from './TaskCard';
import TaskStats from './TaskStats';
import apiService from '../../services/api';

const InBoundTaskBoard = ({ user, selectedCountry = 'all' }) => {
  const [tasks, setTasks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filter, setFilter] = useState('all');
  const [uploadStatus, setUploadStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState('bulk');

  useEffect(() => {
    loadTasks();
  }, [selectedCountry]);

  const loadTasks = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCountry && selectedCountry !== 'all') {
        params.append('country_id', selectedCountry);
      }
      const queryString = params.toString();
      const response = await apiService.get(`/tasks${queryString ? `?${queryString}` : ''}`);

      let tasksData = [];
      if (Array.isArray(response)) {
        tasksData = response;
      } else if (response && Array.isArray(response.data)) {
        tasksData = response.data;
      } else if (response && response.tasks && Array.isArray(response.tasks)) {
        tasksData = response.tasks;
      }

      setTasks(tasksData);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ['.csv', '.xlsx', '.xls'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (allowedTypes.includes(fileExtension)) {
        setSelectedFile(file);
      } else {
        alert('Please select a CSV or Excel file');
        event.target.value = '';
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('Please select a file first');
      setTimeout(() => setUploadStatus(''), 3000);
      return;
    }

    try {
      setUploading(true);
      setUploadStatus('Uploading and processing file...');

      const formData = new FormData();
      formData.append('file', selectedFile);
      if (selectedCountry && selectedCountry !== 'all') {
        formData.append('country_id', selectedCountry);
      }

      const response = await apiService.post('/tasks/upload', formData);

      let message = 'Upload completed!';
      if (response.created > 0) {
        message += ` Created ${response.created} new tasks.`;
      }
      if (response.duplicates > 0) {
        message += ` Skipped ${response.duplicates} duplicate tasks.`;
      }

      setUploadStatus(message);
      setSelectedFile(null);
      document.getElementById('file-input').value = '';
      loadTasks();

      setTimeout(() => setUploadStatus(''), 5000);
    } catch (error) {
      setUploadStatus('Upload failed. Please try again.');
      setTimeout(() => setUploadStatus(''), 3000);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await apiService.delete(`/tasks/${taskId}`);
      loadTasks();
    } catch (error) {
      // Handle error silently
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await apiService.put(`/tasks/${taskId}/status`, { status });
      loadTasks();
    } catch (error) {
      // Handle error silently
    }
  };

  const moveTaskToInbound = async (taskId) => {
    try {
      await apiService.put(`/tasks/${taskId}/move-to-inbound`);
      loadTasks();
    } catch (error) {
      console.error('Failed to move task:', error);
    }
  };

  const getTasksWithOverdue = () => {
    return tasks.map(task => {
      if (task.status !== 'registered') {
        const dueDate = task.due_date ? new Date(task.due_date) : new Date(task.created_at);
        const daysDiff = (Date.now() - dueDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysDiff >= 7) {
          return { ...task, status: 'overdue' };
        }
      }
      return task;
    });
  };

  const getFilteredTasks = (boardType) => {
    const tasksWithOverdue = getTasksWithOverdue();
    let filtered = tasksWithOverdue.filter(task =>
      task.task_board_type === boardType || (!task.task_board_type && boardType === 'bulk')
    );

    if (filter !== 'all') {
      filtered = filtered.filter(task => task.status === filter);
    }

    if (searchTerm) {
      filtered = filtered.filter(task =>
        task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.assignee_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.assignee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        JSON.stringify(task.task_details || {}).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const getPaginatedTasks = (boardType) => {
    const filtered = getFilteredTasks(boardType);
    const startIndex = (currentPage - 1) * tasksPerPage;
    return filtered.slice(startIndex, startIndex + tasksPerPage);
  };

  const getTotalPages = (boardType) => {
    return Math.ceil(getFilteredTasks(boardType).length / tasksPerPage);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: { bg: '#fff3cd', color: '#856404', text: 'Pending' },
      in_progress: { bg: '#cce5ff', color: '#004085', text: 'In Progress' },
      registered: { bg: '#d4edda', color: '#155724', text: 'Completed' },
      overdue: { bg: '#f8d7da', color: '#721c24', text: 'Overdue' }
    };

    const style = statusColors[status] || statusColors.pending;
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: style.bg,
        color: style.color
      }}>
        {style.text}
      </span>
    );
  };

  const renderTaskTable = (boardType) => {
    const paginatedTasks = getPaginatedTasks(boardType);
    const totalPages = getTotalPages(boardType);

    return (
      <div style={{
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: boardType === 'bulk' ? '2fr 1fr 1fr 1fr 1fr 120px' : '2fr 1fr 1fr 1fr 1fr 100px',
          gap: '16px',
          padding: '16px 20px',
          background: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
          fontSize: '14px',
          fontWeight: '600',
          color: '#666'
        }}>
          <div>Task Details</div>
          <div>Assignee</div>
          <div>Status</div>
          <div>Created</div>
          <div>Due Date</div>
          <div>Actions</div>
        </div>

        {paginatedTasks.length > 0 ? (
          paginatedTasks.map((task, index) => (
            <div
              key={task.id || index}
              style={{
                display: 'grid',
                gridTemplateColumns: boardType === 'bulk' ? '2fr 1fr 1fr 1fr 1fr 120px' : '2fr 1fr 1fr 1fr 1fr 100px',
                gap: '16px',
                padding: '16px 20px',
                borderBottom: '1px solid #f0f0f0',
                alignItems: 'center',
                fontSize: '14px'
              }}
            >
              <div>
                <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                  {task.task_details?.title || task.title || 'Untitled Task'}
                </div>
                {(task.task_details?.description || task.description) && (
                  <div style={{ color: '#666', fontSize: '13px' }}>
                    {(task.task_details?.description || task.description).length > 100
                      ? (task.task_details?.description || task.description).substring(0, 100) + '...'
                      : (task.task_details?.description || task.description)}
                  </div>
                )}
                {task.task_details && Object.keys(task.task_details).length > 0 && (
                  <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
                    {Object.entries(task.task_details).slice(0, 2).map(([key, value]) => (
                      <span key={key} style={{ marginRight: '12px' }}>
                        <strong>{key}:</strong> {String(value).substring(0, 30)}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div style={{ fontWeight: '500' }}>
                  {task.assignee_name || 'Unassigned'}
                </div>
                <div style={{ color: '#666', fontSize: '12px' }}>
                  {task.assignee_email}
                </div>
              </div>

              <div>
                {getStatusBadge(task.status)}
              </div>

              <div style={{ color: '#666' }}>
                {formatDate(task.created_at)}
              </div>

              <div style={{ color: '#666' }}>
                {formatDate(task.due_date)}
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <select
                  value={task.status}
                  onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                  style={{
                    padding: '4px 6px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '12px',
                    width: '80px'
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="registered">Completed</option>
                </select>

                {boardType === 'bulk' && user?.role === 'super_admin' && (
                  <button
                    onClick={() => moveTaskToInbound(task.id)}
                    style={{
                      padding: '4px 8px',
                      border: 'none',
                      background: '#28a745',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      whiteSpace: 'nowrap'
                    }}
                    title="Move to Inbound"
                  >
                    → Inbound
                  </button>
                )}

                {user?.role === 'super_admin' && (
                  <button
                    onClick={() => handleDeleteTask(task.id)}
                    style={{
                      padding: '4px 6px',
                      border: 'none',
                      background: '#dc3545',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title="Delete task"
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: '#666'
          }}>
            {searchTerm || filter !== 'all'
              ? 'No tasks match your filters'
              : `No ${boardType} tasks available`}
          </div>
        )}

        {totalPages > 1 && (
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Showing {((currentPage - 1) * tasksPerPage) + 1} to {Math.min(currentPage * tasksPerPage, getFilteredTasks(boardType).length)} of {getFilteredTasks(boardType).length} tasks
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #ddd',
                  background: currentPage === 1 ? '#f5f5f5' : 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                Previous
              </button>

              <span style={{ padding: '6px 12px', fontSize: '14px' }}>
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #ddd',
                  background: currentPage === totalPages ? '#f5f5f5' : 'white',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 24px 0', fontSize: '28px', fontWeight: '600' }}>
          In Bound Task Allocation
        </h1>

        {user?.role === 'super_admin' && (
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            marginBottom: '24px'
          }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              📤 Upload Task Allocation File
            </h2>

            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              Upload CSV or Excel file with task_board_type column (bulk/inbound). Last column should be Assignee Email.<br />
              <strong>Note:</strong> Tasks will be categorized based on task_board_type column value.
            </p>

            <input
              id="file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              style={{
                padding: '8px',
                border: '2px dashed #ddd',
                borderRadius: '8px',
                width: '100%',
                cursor: 'pointer',
                marginBottom: '16px'
              }}
            />

            {selectedFile && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                padding: '8px',
                background: '#f8f9fa',
                borderRadius: '6px'
              }}>
                <span>📄</span>
                <span style={{ fontSize: '14px' }}>{selectedFile.name}</span>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                style={{
                  background: selectedFile && !uploading ? 'var(--primary, #2196f3)' : '#ccc',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: selectedFile && !uploading ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {uploading && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
                {uploading ? 'Processing...' : 'Upload Tasks'}
              </button>
            </div>

            {uploadStatus && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '14px',
                background: uploadStatus.includes('failed') || uploadStatus.includes('Please select')
                  ? '#ffebee' : '#e8f5e8',
                color: uploadStatus.includes('failed') || uploadStatus.includes('Please select')
                  ? '#c62828' : '#2e7d32',
                border: `1px solid ${uploadStatus.includes('failed') || uploadStatus.includes('Please select')
                  ? '#ffcdd2' : '#c8e6c9'}`
              }}>
                {uploadStatus}
              </div>
            )}

            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
      </div>

      <TaskStats tasks={tasks} />

      {/* Controls Bar */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '24px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '200px'
            }}
          />

          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '120px'
            }}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="registered">Completed</option>
            <option value="overdue">Overdue</option>
          </select>

          {(searchTerm || filter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilter('all');
                setCurrentPage(1);
              }}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#666'
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        background: 'white',
        borderRadius: '8px 8px 0 0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <button
            onClick={() => {
              setActiveTab('bulk');
              setCurrentPage(1);
            }}
            style={{
              padding: '16px 24px',
              border: 'none',
              background: activeTab === 'bulk' ? '#2196f3' : 'transparent',
              color: activeTab === 'bulk' ? 'white' : '#666',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              borderBottom: activeTab === 'bulk' ? '3px solid #1976d2' : 'none'
            }}
          >
            Bulk Tasks ({getFilteredTasks('bulk').length})
          </button>
          <button
            onClick={() => {
              setActiveTab('inbound');
              setCurrentPage(1);
            }}
            style={{
              padding: '16px 24px',
              border: 'none',
              background: activeTab === 'inbound' ? '#2196f3' : 'transparent',
              color: activeTab === 'inbound' ? 'white' : '#666',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              borderBottom: activeTab === 'inbound' ? '3px solid #1976d2' : 'none'
            }}
          >
            Inbound Tasks ({getFilteredTasks('inbound').length})
          </button>
        </div>

        <div style={{ background: 'white' }}>
          {renderTaskTable(activeTab)}
        </div>
      </div>
    </div>
  );
};

export default InBoundTaskBoard;